import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState, useEffect } from "react";
import { useToast } from "@/features/toast";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Lock,
  ShieldCheck,
  EyeIcon,
  EyeOffIcon,
  CheckCircle,
  XCircle,
  Loader2,
} from "lucide-react";
import { API_ENDPOINTS } from "@/api/config/api-endpoints";
import { AuthStage } from "@/components/auth/auth-stage";
import {
  authFieldClass,
  authFormClass,
  authLabelClass,
  authLinkClass,
  authPrimaryButtonClass,
  authStackClass,
  authTitleClass,
} from "@/components/auth/auth-ui";
import { AuthIconBadge } from "@/components/auth/auth-icon-badge";
import {
  PasswordRequirements,
  areAllPasswordRequirementsMet,
} from "@/components/auth/password-requirements";

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isTokenValidating, setIsTokenValidating] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { success, error } = useToast();
  const navigate = useNavigate();
  const { id, token } = useParams<{ id: string; token: string }>();
  const arePasswordRequirementsMet = areAllPasswordRequirementsMet(password);

  useEffect(() => {
    const validateToken = async () => {
      if (!id || !token) {
        error(t("auth.resetLinkExpired"), {
          title: t("auth.invalidResetLink"),
          actions: [
            {
              label: t("auth.backToForgotPassword"),
              action: () => navigate("/forgot-password"),
            },
          ],
        });
        navigate("/forgot-password");
        return;
      }

      setIsTokenValidating(true);
      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_BASE_URL}${API_ENDPOINTS.getMe}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: token,
            },
          }
        );

        if (response.ok) {
          setIsTokenValid(true);
        } else {
          throw new Error("Token validation failed");
        }
      } catch (errorResponse: unknown) {
        console.error("Token validation error:", errorResponse);

        error(t("auth.resetLinkExpired"), {
          title: t("auth.invalidResetLink"),
          actions: [
            {
              label: t("auth.backToForgotPassword"),
              action: () => navigate("/forgot-password"),
            },
          ],
        });
        navigate("/forgot-password");
      } finally {
        setIsTokenValidating(false);
      }
    };

    validateToken();
  }, [id, token, navigate, t]);

  // Şifre sıfırla
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!arePasswordRequirementsMet) {
      error(t("auth.passwordMinLength"), {
        title: t("auth.passwordError"),
      });
      return;
    }

    if (password !== confirmPassword) {
      error(t("auth.passwordsDoNotMatch"), {
        title: t("auth.passwordError"),
      });
      return;
    }

    // Modern Promise Integration with Toast
    const resetPasswordPromise = fetch(
      `${import.meta.env.VITE_API_BASE_URL}${API_ENDPOINTS.resetPassword}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: token }),
        },
        body: JSON.stringify({
          id,
          newPassword: password,
        }),
      }
    ).then(async (response) => {
      if (response.ok) {
        navigate("/login");
        return response;
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Reset password failed");
      }
    });

    // Use modern promise integration
    setIsLoading(true);
    try {
      await resetPasswordPromise;
      success(t("auth.passwordUpdated"), {
        title: t("auth.passwordResetSuccess"),
      });
    } catch (errorResponse: any) {
      error(errorResponse.message || t("error.somethingWentWrong"), {
        title: t("auth.passwordResetError"),
        actions: [
          {
            label: t("common.retry"),
            action: () => handleResetPassword(e),
          },
        ],
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Token doğrulanıyor ekranı
  if (isTokenValidating) {
    return (
      <AuthStage>
        <div className="flex flex-col items-center gap-5">
          <AuthIconBadge>
            <Loader2 className="h-6 w-6 animate-spin text-text-primary dark:text-text-primary" />
          </AuthIconBadge>
          <div className="text-center space-y-2">
            <h1 className={authTitleClass}>
              {t("auth.validatingResetLink")}
            </h1>
            <p className="text-sm text-muted-foreground">{t("auth.pleaseWait")}</p>
          </div>
        </div>
      </AuthStage>
    );
  }

  // Token geçersizse bu component zaten yönlendirme yapar,
  // ama güvenlik için ayrıca kontrol et
  if (!isTokenValid) {
    return null;
  }

  return (
    <AuthStage>
          <div className={authStackClass}>
            <div className="flex flex-col items-center gap-4">
              <AuthIconBadge
                variant={arePasswordRequirementsMet ? "success" : "default"}
              >
                {arePasswordRequirementsMet ? (
                  <ShieldCheck className="h-6 w-6 text-text-success dark:text-text-success" />
                ) : (
                  <Lock className="h-6 w-6 text-text-primary dark:text-text-primary" />
                )}
              </AuthIconBadge>
              <div className="text-center space-y-2">
                <h1 className={authTitleClass}>
                  {t("auth.setNewPassword")}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {t("auth.setNewPasswordDescription")}
                </p>
              </div>
            </div>

            <form onSubmit={handleResetPassword} className={authFormClass}>
              <div className="space-y-1">
                <label
                  htmlFor="password"
                  className={authLabelClass}
                >
                  {t("auth.newPassword")}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder={t("auth.enterNewPassword")}
                    className={`${authFieldClass} pl-9 pr-10`}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? (
                      <EyeOffIcon className="h-4 w-4" />
                    ) : (
                      <EyeIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <PasswordRequirements password={password} />
              </div>

              <div className="space-y-1">
                <label
                  htmlFor="confirmPassword"
                  className={authLabelClass}
                >
                  {t("auth.confirmPassword")}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder={t("auth.confirmNewPassword")}
                    className={`${authFieldClass} pl-9 pr-10`}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showConfirmPassword ? (
                      <EyeOffIcon className="h-4 w-4" />
                    ) : (
                      <EyeIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {confirmPassword && (
                  <div className="flex items-center gap-1.5 mt-2">
                    {password === confirmPassword ? (
                      <>
                        <CheckCircle className="h-3.5 w-3.5 text-text-success dark:text-text-success" />
                        <span className="text-xs text-text-success dark:text-text-success">
                          {t("auth.passwordsMatch")}
                        </span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-3.5 w-3.5 text-text-error dark:text-text-error" />
                        <span className="text-xs text-text-error dark:text-text-error">
                          {t("auth.passwordsDoNotMatch")}
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>

              <Button
                type="submit"
                variant="darkPrimary"
                className={authPrimaryButtonClass}
                disabled={
                  isLoading ||
                  !password ||
                  !confirmPassword ||
                  password !== confirmPassword ||
                  !arePasswordRequirementsMet
                }
              >
                {isLoading ? t("common.loading") : t("auth.updatePassword")}
              </Button>
            </form>

            {/* Back to Login */}
            <div className="flex justify-center">
              <Link
                to="/login"
                className={`${authLinkClass} text-sm`}
              >
                {t("auth.backToLogin")}
              </Link>
            </div>

          </div>
    </AuthStage>
  );
}
